import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useRef } from 'react';
import { Resolver, useForm, useWatch } from 'react-hook-form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BankAccountFormatRuleRes } from '@dealstack/shared/lib/generated';
import { BANK_ACCOUNT_FORM_FIELDS, BankAccountFormValues } from './types.ts';
import { buildBankAccountShape } from './bankAccountShape.ts';
import { inferFormatRule, ruleUsesClassicIbanForm } from './BankAccountForm.utils.ts';
import { getBankAccountFormatRules } from '@dealstack/frontend/shared/dataExchange/services/bankAccountFormatRule.service.ts';
import { VERIFY_QUERY_KEY } from './useBankAccountVerification.ts';
import type { UseBankAccountFormParams } from './useBankAccountForm.types.ts';

// The zod shape is rebuilt per rule, never per keystroke: react-query keeps the rule
// objects referentially stable, so they key the cache.
const classicResolver: Resolver<BankAccountFormValues> = zodResolver(buildBankAccountShape(null));
const ruleResolvers = new WeakMap<BankAccountFormatRuleRes, Resolver<BankAccountFormValues>>();
const resolverFor = (rule: BankAccountFormatRuleRes | null): Resolver<BankAccountFormValues> => {
	if (!rule) {
		return classicResolver;
	}
	const cached = ruleResolvers.get(rule);
	if (cached) {
		return cached;
	}
	const resolver: Resolver<BankAccountFormValues> = zodResolver(buildBankAccountShape(rule));
	ruleResolvers.set(rule, resolver);
	return resolver;
};

const FORM_DEFAULT_VALUES = {
	[BANK_ACCOUNT_FORM_FIELDS.iban]: '',
	[BANK_ACCOUNT_FORM_FIELDS.bank_name]: '',
	[BANK_ACCOUNT_FORM_FIELDS.country_code]: null,
	[BANK_ACCOUNT_FORM_FIELDS.verified_country_code]: null,
	[BANK_ACCOUNT_FORM_FIELDS.currency]: null,
	[BANK_ACCOUNT_FORM_FIELDS.account_name]: '',
	[BANK_ACCOUNT_FORM_FIELDS.account_number]: '',
	[BANK_ACCOUNT_FORM_FIELDS.bic]: '',
	[BANK_ACCOUNT_FORM_FIELDS.format_rule_id]: null,
	[BANK_ACCOUNT_FORM_FIELDS.identifiers]: {},
};

export const useBankAccountForm = ({
	inferredCountryCode,
	bankAccount,
}: UseBankAccountFormParams = {}) => {
	const queryClient = useQueryClient();

	// The active schema depends on the selected format rule; a ref-backed resolver keeps
	// a single stable function while always validating against the current rule.
	const activeRuleRef = useRef<BankAccountFormatRuleRes | null>(null);
	const resolver = useCallback<Resolver<BankAccountFormValues>>(
		(values, context, options) => resolverFor(activeRuleRef.current)(values, context, options),
		[]
	);

	const bankAccountForm = useForm<BankAccountFormValues>({
		mode: 'all',
		resolver,
		defaultValues: {
			...FORM_DEFAULT_VALUES,
			[BANK_ACCOUNT_FORM_FIELDS.country_code]: inferredCountryCode ?? null,
		},
	});

	const selectedCountryCode = useWatch({
		control: bankAccountForm.control,
		name: BANK_ACCOUNT_FORM_FIELDS.country_code,
	});
	const selectedFormatRuleId = useWatch({
		control: bankAccountForm.control,
		name: BANK_ACCOUNT_FORM_FIELDS.format_rule_id,
	});

	const formatRulesRequest = useQuery({
		queryKey: ['bankAccountFormatRules', selectedCountryCode],
		queryFn: () => getBankAccountFormatRules(selectedCountryCode ?? ''),
		enabled: !!selectedCountryCode,
		staleTime: Infinity,
	});
	// An errored fetch must not read as "this country has no formats" — that would
	// render the classic IBAN form for a country whose rules the user has to satisfy.
	const formatRulesError = !!selectedCountryCode && formatRulesRequest.isError;
	const formatRules =
		selectedCountryCode && !formatRulesError ? formatRulesRequest.data ?? [] : [];

	const activeRule = formatRules.find((rule) => rule.id === selectedFormatRuleId) ?? null;
	activeRuleRef.current = activeRule;

	// Declared ahead of the tab seeding below: with cached rules both run in the same
	// commit, and the seeding must read the reset values, not the defaults.
	useEffect(() => {
		if (bankAccount) {
			const identifiers = bankAccount.identifiers ?? [];
			// Classic fields only prefill from values entered through the classic path
			// (unlinked rows); values linked to a format rule prefill that rule's tab
			// via the identifiers record instead, so formats never leak into each other.
			const classicValue = (key: string) =>
				identifiers.find(
					(identifier) => identifier.key === key && !identifier.bankAccountFormatRuleId
				)?.value ?? '';
			bankAccountForm.reset({
				bankName: classicValue('bank_name'),
				accountName: bankAccount.accountHolder ?? '',
				accountNumber: classicValue('account_number'),
				iban: identifiers.find((identifier) => identifier.key === 'iban')?.value ?? '',
				bic: classicValue('bic'),
				countryCode: bankAccount.countryCode ?? inferredCountryCode ?? null,
				currency: bankAccount.currency ?? null,
				formatRuleId: null,
				identifiers: Object.fromEntries(
					identifiers
						.filter((identifier) => identifier.bankAccountFormatRuleId)
						.map(({ key, value }) => [key, value])
				),
			});
		}
	}, [bankAccount]);

	const accountIdentifiers = bankAccount?.identifiers ?? [];
	const lastShapedRuleIdRef = useRef<string | null>(null);
	// What was in each rule's fields when its tab was left. Reshaping drops the keys of
	// the tab being left, so without this a return visit has nothing to read back and
	// reseeds from the stored account — silently undoing the edit that prompted it.
	const draftsByRuleIdRef = useRef<Record<string, Record<string, string>>>({});

	useEffect(() => {
		if (!formatRules.length) {
			return;
		}
		const currentIdentifiers =
			bankAccountForm.getValues(BANK_ACCOUNT_FORM_FIELDS.identifiers) ?? {};
		// Tab inference only: the heuristic matches on which keys the account holds, so
		// it must see every stored identifier — a migrated row carries no rule link but
		// still says which format the account was entered under. Kept separate from
		// currentIdentifiers, which drives the rule-scoped prefill below and must not
		// gain unlinked values.
		const heuristicIdentifiers = {
			...Object.fromEntries(accountIdentifiers.map(({ key, value }) => [key, value])),
			...currentIdentifiers,
		};
		const linkedRuleIds = new Set(
			accountIdentifiers
				.map((identifier) => identifier.bankAccountFormatRuleId)
				.filter((ruleId): ruleId is string => !!ruleId)
		);
		const rule =
			formatRules.find((candidate) => candidate.id === selectedFormatRuleId) ??
			inferFormatRule(formatRules, heuristicIdentifiers, linkedRuleIds);
		if (rule?.id !== selectedFormatRuleId) {
			bankAccountForm.setValue(BANK_ACCOUNT_FORM_FIELDS.format_rule_id, rule?.id ?? null);
		}

		if (!rule || ruleUsesClassicIbanForm(rule)) {
			return;
		}

		// Seeded by key alone, not by which rule the value was linked to. An account
		// holds exactly one active format rule (a full submission relinks every key it
		// writes and sweeps the rest) and `(bankAccountId, key)` is uniquely indexed
		// while live, so a stored key is unambiguous — it belongs to the account, not to
		// the tab it happened to be entered on. Scoping it to the current rule blanked
		// every stored field on every other tab: BR's Conta Corrente/Poupanca differ
		// only by account_type, and CA's two tabs share account_number.
		const stored = Object.fromEntries(
			accountIdentifiers.map((identifier) => [identifier.key, identifier.value])
		);
		const previousRuleId = lastShapedRuleIdRef.current;
		const isRuleTransition = previousRuleId !== rule.id;
		if (isRuleTransition && previousRuleId) {
			draftsByRuleIdRef.current[previousRuleId] = currentIdentifiers;
		}
		const draft = draftsByRuleIdRef.current[rule.id];
		const seedValue = (key: string): string => {
			if (!isRuleTransition) {
				return currentIdentifiers[key] ?? '';
			}
			// A return visit restores what was typed here; only a first visit falls back
			// to the stored account.
			if (draft?.[key] !== undefined) {
				return draft[key] ?? '';
			}
			if (stored[key] !== undefined) {
				return stored[key] || '';
			}
			return currentIdentifiers[key] ?? '';
		};
		const seeded = Object.fromEntries(
			rule.fields.map((field) => [
				field.key,
				field.value !== undefined ? field.value : seedValue(field.key),
			])
		);
		const alreadyShaped =
			Object.keys(currentIdentifiers).length === rule.fields.length &&
			rule.fields.every((field) => currentIdentifiers[field.key] === seeded[field.key]);
		if (!alreadyShaped) {
			bankAccountForm.setValue(BANK_ACCOUNT_FORM_FIELDS.identifiers, seeded);
		}
		lastShapedRuleIdRef.current = rule.id;
	}, [formatRulesRequest.data, selectedFormatRuleId, selectedCountryCode]);

	// A country change swaps which formats are on offer, not the account's data, so what
	// was entered stays: the seeding effect reshapes `identifiers` to the new country's
	// field keys and the zod shape drops the rest, so nothing out of scope can be saved.
	// Lookup-derived state is the exception — it describes a bank in the country being
	// left, and verifiedCountryCode would otherwise overwrite the newly chosen country
	// on save (toBankAccountPayload prefers it over the picked one).
	const onCountryChange = (nextCountryCode: string | null) => {
		if (nextCountryCode === selectedCountryCode) {
			return;
		}

		bankAccountForm.setValue(BANK_ACCOUNT_FORM_FIELDS.country_code, nextCountryCode);
		bankAccountForm.setValue(BANK_ACCOUNT_FORM_FIELDS.verified_country_code, null);
		queryClient.removeQueries({ queryKey: [VERIFY_QUERY_KEY] });
	};

	const onFormatChange = (nextFormatRuleId: string | null) => {
		if (!nextFormatRuleId || nextFormatRuleId === selectedFormatRuleId) {
			return;
		}

		const nextRule = formatRules.find((rule) => rule.id === nextFormatRuleId);

		bankAccountForm.setValue(BANK_ACCOUNT_FORM_FIELDS.format_rule_id, nextFormatRuleId);
		bankAccountForm.setValue(BANK_ACCOUNT_FORM_FIELDS.verified_country_code, null);

		if (nextRule && !ruleUsesClassicIbanForm(nextRule)) {
			bankAccountForm.setValue(BANK_ACCOUNT_FORM_FIELDS.bank_name, '');
			bankAccountForm.setValue(BANK_ACCOUNT_FORM_FIELDS.bic, '');
		}
		queryClient.removeQueries({ queryKey: [VERIFY_QUERY_KEY] });
	};

	// A stored summary is only still vouched for while the form shows the country AND
	// the format it was validated under. Change either and the values stay on screen —
	// nothing resets now — but they describe a check that no longer applies, so the card
	// drops its success outline and reads as history.
	const savedFormatRuleId =
		(bankAccount?.identifiers ?? []).find((identifier) => identifier.bankAccountFormatRuleId)
			?.bankAccountFormatRuleId ?? null;
	const storedSummaryValidated =
		!!bankAccount &&
		!!selectedCountryCode &&
		bankAccount.countryCode === selectedCountryCode &&
		// A classic path carries no selected rule, and an account saved before any rule
		// existed carries no link — neither is a format change, so compare only when
		// both sides name one.
		(!selectedFormatRuleId || !savedFormatRuleId || selectedFormatRuleId === savedFormatRuleId);

	return {
		bankAccountForm,
		storedSummaryValidated,
		selectedCountryCode,
		onCountryChange,
		formatRules,
		formatRulesLoading: !!selectedCountryCode && formatRulesRequest.isLoading,
		formatRulesError,
		activeRule,
		selectedFormatRuleId,
		onFormatChange,
	};
};
